import { LoadingButton } from '@mui/lab'
import { Box, Button, FormControl, TextField, Typography } from '@mui/material'
import { matchIsValidTel, MuiTelInput } from 'mui-tel-input'
import React, { useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import {
  GuestAdoptionCheckoutAdopterDto,
  GuestAdoptionContactBodyDto,
} from '../../interactors/clients/GuestAdoptionsClient'
import { omitEmptyString } from '../../utils/omitEmptyString'
import { Field } from '../common/FormField'
import { InfoBox } from '../common/InfoBox'
import { GuestAdoptionCard, GuestAdoptionTitle } from './GuestAdoptionLayout'

interface FormParams {
  firstName: string
  lastName: string
  phoneNumber: string
  address: string
  postalCode: string
  city: string
  country: string
}

interface Props {
  adopter: GuestAdoptionCheckoutAdopterDto
  onSubmit: (contact: GuestAdoptionContactBodyDto) => Promise<void>
  onCancel: () => void
}

export const GuestAdoptionContactForm: React.FC<Props> = ({ adopter, onSubmit, onCancel }) => {
  const [errored, setErrored] = useState<boolean>(false)

  const {
    register,
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormParams>({
    defaultValues: {
      firstName: adopter.firstName ?? '',
      lastName: adopter.lastName ?? '',
      phoneNumber: adopter.phoneNumber ?? '',
      address: adopter.address ?? '',
      postalCode: adopter.postalCode ?? '',
      city: adopter.city ?? '',
      country: adopter.country ?? '',
    },
  })

  const submit: SubmitHandler<FormParams> = async (data) => {
    setErrored(false)

    const contact = omitEmptyString({
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      phoneNumber: ['+33', ''].includes(data.phoneNumber.trim()) ? '' : data.phoneNumber,
      address: data.address.trim(),
      postalCode: data.postalCode.trim(),
      city: data.city.trim(),
      country: data.country.trim(),
    })

    try {
      await onSubmit(contact)
    } catch {
      setErrored(true)
    }
  }

  return (
    <GuestAdoptionCard>
      <GuestAdoptionTitle>Vos coordonnées</GuestAdoptionTitle>
      <Typography variant="body2" sx={{ color: '#696B6D' }}>
        Facultatif : ces informations facilitent le suivi de votre adoption par l&apos;association.
      </Typography>
      {errored && (
        <InfoBox
          messageType="error"
          content="Vos coordonnées n'ont pas pu être enregistrées. Vérifiez les informations saisies, notamment le numéro de téléphone."
        />
      )}
      <form onSubmit={handleSubmit(submit)} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Field label="Email">
          <TextField
            aria-label="Email"
            type="email"
            value={adopter.email}
            fullWidth
            hiddenLabel
            variant="outlined"
            disabled
            helperText="Votre email est l'identifiant partagé avec l'association : il n'est pas modifiable."
          />
        </Field>
        <Field label="Prénom">
          <TextField aria-label="Prénom" type="text" {...register('firstName')} fullWidth hiddenLabel />
        </Field>
        <Field label="Nom">
          <TextField aria-label="Nom" type="text" {...register('lastName')} fullWidth hiddenLabel />
        </Field>
        <Field label="Téléphone">
          <FormControl variant="outlined" sx={{ width: '100%' }}>
            <Controller
              name="phoneNumber"
              control={control}
              rules={{
                validate: (value: string | undefined) => {
                  return !value || value.trim() === '' || matchIsValidTel(value) || value.trim() === '+33'
                    ? true
                    : 'Tel is invalid'
                },
              }}
              render={({ field, fieldState }) => (
                <MuiTelInput
                  {...field}
                  aria-label="Numéro de Téléphone"
                  defaultCountry="FR"
                  helperText={fieldState.error ? "Le numéro de téléphone n'est pas valide" : ''}
                  error={!!fieldState.error}
                  hiddenLabel
                />
              )}
            />
          </FormControl>
        </Field>
        <Field label="Adresse">
          <TextField aria-label="Adresse" placeholder="Votre adresse" {...register('address')} hiddenLabel fullWidth />
        </Field>
        <Field label="Code Postal">
          <TextField
            aria-label="Code Postal"
            placeholder="Votre code postal"
            {...register('postalCode')}
            hiddenLabel
            fullWidth
          />
        </Field>
        <Field label="Ville">
          <TextField aria-label="Ville" placeholder="Votre ville" {...register('city')} hiddenLabel fullWidth />
        </Field>
        <Field label="Pays">
          <TextField aria-label="Pays" placeholder="Votre pays" {...register('country')} hiddenLabel fullWidth />
        </Field>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          <LoadingButton
            variant="contained"
            color="primary"
            type="submit"
            fullWidth
            loading={isSubmitting}
            sx={{ fontSize: '18px' }}
          >
            Enregistrer
          </LoadingButton>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            disabled={isSubmitting}
            onClick={onCancel}
            sx={{ fontSize: '18px' }}
          >
            Annuler
          </Button>
        </Box>
      </form>
    </GuestAdoptionCard>
  )
}
